import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { trackEvent, appState, syncTelegramUser, trackAdsPageView } from "@/lib/analytics";
import { initTelegram } from "@/lib/telegram";
import { useScrollReveal } from "@/lib/useScrollReveal";
import type { ProductCategory } from "@/data/products";

// Above-the-fold: ship in the initial bundle so LCP is unaffected.
import Header from "@/components/Header";
import HeroSection from "@/components/HeroSection";
import TrustBlock from "@/components/TrustBlock";
import UrgencyBar from "@/components/UrgencyBar";
import WhyChooseUsSection from "@/components/WhyChooseUsSection";
import ProductGrid from "@/components/ProductGrid";
import Footer from "@/components/Footer";
import MobileStickyCTA from "@/components/MobileStickyCTA";
import ARButton from "@/components/ARPreview/ARButton";

// Below-the-fold: lazy-loaded to shrink the critical JS payload.
const PartnersSection = lazy(() => import("@/components/PartnersSection"));
const WhyUsSection = lazy(() => import("@/components/WhyUsSection"));
const HowWeWorkSection = lazy(() => import("@/components/HowWeWorkSection"));
const DiyBlock = lazy(() => import("@/components/DiyBlock"));
const FaqSection = lazy(() => import("@/components/FaqSection"));
const ConversionBlock = lazy(() => import("@/components/ConversionBlock"));
const PwaInstallPrompt = lazy(() => import("@/components/PwaInstallPrompt"));
const ProblemSolutionSection = lazy(() => import("@/components/ProblemSolutionSection"));
const LocalContactsSection = lazy(() => import("@/components/LocalContactsSection"));
const RealWorksSection = lazy(() => import("@/components/RealWorksSection"));
const ReviewsSection = lazy(() => import("@/components/ReviewsSection"));
const DecisionGuideSection = lazy(() => import("@/components/DecisionGuideSection"));
const BeforeAfterSection = lazy(() => import("@/components/BeforeAfterSection"));
const DistrictTrustSection = lazy(() => import("@/components/DistrictTrustSection"));
const FinalDecisionPush = lazy(() => import("@/components/FinalDecisionPush"));

const ProductModal = lazy(() => import("@/components/ProductModal"));
const LeadForm = lazy(() => import("@/components/LeadForm"));
const ARPreview = lazy(() => import("@/components/ARPreview/ARPreview"));
const MeasurementModal = lazy(() => import("@/components/MeasurementModal"));

const Index = () => {
  const [selectedProduct, setSelectedProduct] = useState<ProductCategory | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formContext, setFormContext] = useState("");
  const [arOpen, setArOpen] = useState(false);
  const [arInitialProductId, setArInitialProductId] = useState<string | undefined>(undefined);
  const [arResult, setArResult] = useState<{ url: string; blind: string; color: string; code?: string; hex?: string } | null>(null);
  const [measurementOpen, setMeasurementOpen] = useState(false);

  useEffect(() => {
    initTelegram();
    syncTelegramUser();
    trackEvent("open_app");
    trackAdsPageView();
  }, []);

  const mainRef = useRef<HTMLElement>(null);
  useScrollReveal(mainRef);

  const handleAction = (action: string) => {
    appState.source_button = action;
    // Route measurement-style intents to the dedicated MeasurementModal
    if (
      action === "measurement" ||
      action === "sticky_cta_calc" ||
      action === "why_us_cta" ||
      action === "urgency_book_measurement"
    ) {
      trackEvent("open_measurement_modal", { source: action });
      setMeasurementOpen(true);
      return;
    }
    trackEvent("open_lead_form", { lead_type: action });
    setFormContext(action);
    setShowForm(true);
  };

  const handleProductSelect = (product: ProductCategory) => {
    appState.viewed_product = product.id;
    appState.viewed_category = product.id;
    trackEvent("view_product", { product_id: product.id, product_title: product.title });
    setSelectedProduct(product);
  };

  const openAR = (productId?: string) => {
    trackEvent("ar_open", { product: productId || "" });
    setArInitialProductId(productId);
    setArOpen(true);
  };

  const handleArSend = (url: string, blind: string, color: string, code?: string, hex?: string) => {
    setArOpen(false);
    setArResult({ url, blind, color, code, hex });
    setFormContext("ar_preview");
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setArResult(null);
  };

  return (
    <div id="top" className="min-h-screen bg-background">
      <Header onAction={handleAction} />

      <div className="max-w-[1400px] 2xl:max-w-[1560px] mx-auto pb-24 md:pb-20 md:px-10 lg:px-14 xl:px-16">
        <main ref={mainRef} className="flex flex-col gap-8 md:gap-16 pt-4 md:pt-8">
          {/* Critical above-the-fold */}
          <HeroSection onAction={handleAction} />
          <UrgencyBar onBook={() => handleAction("urgency_book_measurement")} />
          <TrustBlock />
          <WhyChooseUsSection onAction={handleAction} />
          <section id="products"><ProductGrid onSelect={handleProductSelect} /></section>

          {/* Below-the-fold — lazy chunks; placeholder reserves no specific height
              (a min-h placeholder would prevent layout shift but the existing flex gap
              already keeps things smooth). */}
          <Suspense fallback={<div style={{ minHeight: 200 }} />}>
            <RealWorksSection />
            <BeforeAfterSection />
            <ProblemSolutionSection />
            <DecisionGuideSection onAction={handleAction} />
            <PartnersSection />
            <section id="why-us"><WhyUsSection /></section>
            <section id="how"><HowWeWorkSection /></section>
            <LocalContactsSection />
            <DistrictTrustSection />
            <ReviewsSection />
            <DiyBlock />
            <section id="faq"><FaqSection onAction={handleAction} /></section>
            <div className="px-4 md:px-0 md:hidden">
              <ARButton onClick={() => openAR()} variant="primary" />
            </div>
            <FinalDecisionPush onAction={handleAction} />
            <ConversionBlock />
          </Suspense>
        </main>
      </div>

      <Footer />
      <MobileStickyCTA onCalc={() => handleAction("sticky_cta_calc")} />
      <Suspense fallback={null}>
        <PwaInstallPrompt />
      </Suspense>

      {selectedProduct && (
        <Suspense fallback={null}>
          <ProductModal
            product={selectedProduct}
            onClose={() => setSelectedProduct(null)}
            onAction={(action) => {
              if (action === "ar") {
                const pid = selectedProduct.id;
                setSelectedProduct(null);
                openAR(pid);
                return;
              }
              setSelectedProduct(null);
              handleAction(action);
            }}
          />
        </Suspense>
      )}

      {showForm && (
        <Suspense fallback={null}>
          <LeadForm
            context={formContext}
            onClose={closeForm}
            arScreenshotUrl={arResult?.url}
            arMeta={arResult ? { blind: arResult.blind, color: arResult.color } : undefined}
            selectedColorCode={arResult?.code}
            selectedColorHex={arResult?.hex}
          />
        </Suspense>
      )}

      {arOpen && (
        <Suspense fallback={null}>
          <ARPreview
            initialProductId={arInitialProductId}
            onClose={() => setArOpen(false)}
            onSendLead={handleArSend}
          />
        </Suspense>
      )}

      {measurementOpen && (
        <Suspense fallback={null}>
          <MeasurementModal open={measurementOpen} onClose={() => setMeasurementOpen(false)} />
        </Suspense>
      )}
    </div>
  );
};

export default Index;
